/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import { Box, Menu, MenuItem, Tooltip } from "@material-ui/core";
import { Info } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { CartItemData } from "../../../../../models/cartData";
import { EventStatus } from "../../../../../models/enums/eventStatus.enum";
import { InternalExternalEventType } from "../../../../../models/enums/eventType.enum";
import { ParticipationStatus } from "../../../../../models/enums/participationStatus.enum";
import { TrainingType } from "../../../../../models/enums/trainingType.enum";
import { UserType } from "../../../../../models/enums/userType.enum";
import { EventListData } from "../../../../../models/eventData";
import {
  LinkAccountData,
  LinkAccountStatus,
} from "../../../../../models/linkAccountData";
import { UserData } from "../../../../../models/userData";
import { AppState } from "../../../../../redux";
import { addEventToCart } from "../../../../../redux/cart/actions";
import SelfBookingAddressRequiredDialog from "../../../../layout/dialog/SelfBookingAddressRequiredDialog";
import routes from "../../../../../routing/routes";
import { EventItemBookingButton } from "./EventItemBookingButton";
import TargetGroupsDontMatchPopover from "./TargetGroupsDontMatchPopover";
import { useTranslation } from "react-i18next";

interface EventItemRenderButtonProps {
  eventData: EventListData;
  eventWorkload: number;
}

const EventItemRenderButton: React.FunctionComponent<EventItemRenderButtonProps> = (
  props
) => {
  const { t } = useTranslation(["events", "snackbars"]);
  const { eventData, eventWorkload } = props;
  const [anchorElWrongTargetGroup, setAnchorElWrongTargetGroup] =
    React.useState<HTMLElement | null>(null);
  const openWrongTargetGroup = Boolean(anchorElWrongTargetGroup);

  const handlePopoverOpenWrongTargetGroup = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setAnchorElWrongTargetGroup(event.currentTarget);
  };

  const handlePopoverCloseWrongTargetGroup = () => {
    setAnchorElWrongTargetGroup(null);
  };

  const [bookingForAnchorEl, setBookingForAnchorEl] = useState<null | HTMLElement>(null);
  const [isBookedForMe, setIsBookedForMe] = useState<boolean>(true);

  const auth = useSelector((state: AppState) => state.auth);
  const cart = useSelector((state: AppState) => state.cart);
  const currentUser = useSelector((state: AppState) => state.user.currentUser);
  const userSelfBookingFeatureEnabled = useSelector(
    (state: AppState) =>
      state.featureFlagging.featureFlags.user_self_booking_feature_is_enabled
  );
  const linkAccount = useSelector((state: AppState) => state.linkAccount);
  const targetGroups = useSelector((state: AppState) => state.targetGroups.targetGroups);
  const eventIsFullyBooked =
    (eventWorkload === 100 ||
      eventData.status === EventStatus.Full ||
      eventData.seats_taken >= eventData.seats) &&
    eventData.training_type !== TrainingType.ELearning;

  const linkedAccountsLength = linkAccount.linkAccountList.filter(
    (x) => x.status === LinkAccountStatus.Linked
  ).length;

  const history = useHistory();
  const dispatch = useDispatch();

  const hasCompleteAddress = Boolean(
    currentUser?.address?.street?.trim() &&
      currentUser?.address?.street_number?.trim() &&
      currentUser?.address?.zip_code?.trim() &&
      currentUser?.address?.city?.trim()
  );

  /**
   * Redirects the user to the profile settings page after confirming the address hint.
   */
  const redirectToProfileSettings = () => {
    dispatch(SelfBookingAddressRequiredDialog(() => history.push(routes.settings)));
  };

  function checkTargetGroupMatch(user: UserData | LinkAccountData | undefined) {
    if (user) {
      return eventData.is_my_target_group ? eventData.is_my_target_group : false;
    }
    return !auth.loggedIn;
  }

  const handleBookingForClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setBookingForAnchorEl(event.currentTarget);
  };

  const handleBookingForClose = () => {
    setBookingForAnchorEl(null);
  };

  function setButtonName(btnProps: unknown) {
    return `bookEvent_${eventData?.vnr}`;
  }

  // one user at a time
  function isAllowedToBook(bookforMyself: boolean, linkedUser?: LinkAccountData) {
    if (bookforMyself) {
      if (cart.cart.cart_items.find((x) => x.book_for?.id !== currentUser?.id)) {
        return false;
      }
      return true;
    } else {
      // book for someone else
      if (
        cart.cart.cart_items.find(
          (x) => x.book_for?.id === currentUser?.id || x.book_for?.id !== linkedUser?.id
        )
      ) {
        return false;
      }
      return true;
    }
  }

  const isInCart = (
    eventId: string,
    bookedForMe = true,
    linkedUser?: LinkAccountData
  ) => {
    if (
      cart.cart.cart_items.find((cartItem: CartItemData) => {
        if (bookedForMe) {
          if (
            cartItem.id === eventId &&
            //cartItem.book_for.id === currentUser?.id
            cartItem.book_for.id === currentUser?.id
          ) {
            return true;
          }
          return false;
        } else if (linkedUser) {
          if (
            cartItem.id === eventId &&
            cartItem.book_for.id !== currentUser?.id &&
            linkedUser.id === cartItem.book_for.id
          ) {
            return true;
          }
          return false;
        }
        return false;
      })
    ) {
      return true;
    }
    return false;
  };

  if (linkedAccountsLength === 0) {
    const statusText =
      eventData.participation_status === ParticipationStatus.Booked
        ? t("status.booked", { ns: "events" })
        : eventData.participation_status === ParticipationStatus.PendingPayment
          ? t("status.pendingPayment", { ns: "events" })
          : null;
    if (statusText) {
      return (
        <EventItemBookingButton
          id={setButtonName(props)}
          disabled
          color="primary"
          text={statusText}
        />
      );
    }
  }
  // TODO ADD PREPAYMENT STATUS

  if (isInCart(eventData.id) && linkedAccountsLength === 0) {
    return (
      <EventItemBookingButton
        id={setButtonName(props)}
        disabled
        color="secondary"
        text={t("status.inCart", { ns: "events" })}
      />
    );
  }

  if (
    eventData.participation_status === ParticipationStatus.Participated &&
    linkedAccountsLength === 0
  ) {
    return (
      <EventItemBookingButton
        id={setButtonName(props)}
        disabled
        color="secondary"
        text={t("status.participated", { ns: "events" })}
      />
    );
  }

  if (
    props?.eventData.internal_external === InternalExternalEventType.Internal ||
    targetGroups.participation_types.length === 0
  ) {
    return (
      <>
        <TargetGroupsDontMatchPopover
          openWrongTargetGroup={openWrongTargetGroup}
          anchorElWrongTargetGroup={anchorElWrongTargetGroup}
          handlePopoverCloseWrongTargetGroup={() => handlePopoverCloseWrongTargetGroup()}
          book_for_me={isBookedForMe}
        />
        <Menu
          id="bookingFor-menu"
          anchorEl={bookingForAnchorEl}
          keepMounted
          open={Boolean(bookingForAnchorEl)}
          onClose={handleBookingForClose}
        >
          <Box
            component="div"
            display="flex"
            alignItems="center"
            onMouseEnter={(event) => {
              setIsBookedForMe(true);
              return !checkTargetGroupMatch(currentUser)
                ? handlePopoverOpenWrongTargetGroup(event)
                : undefined;
            }}
            onMouseLeave={
              !checkTargetGroupMatch(currentUser)
                ? handlePopoverCloseWrongTargetGroup
                : undefined
            }
          >
            <MenuItem
              style={{
                width:
                  !isAllowedToBook(true) &&
                  eventData.participation_status !== ParticipationStatus.Booked
                    ? "auto"
                    : "100%",
              }}
              key="book-for-me-menu-item"
              disabled={
                !checkTargetGroupMatch(currentUser) ||
                eventData.participation_status === ParticipationStatus.Booked ||
                eventIsFullyBooked ||
                isInCart(eventData.id) ||
                !isAllowedToBook(true)
              }
              onClick={(event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                handleBookingForClose();
                if (currentUser) {
                  if (userSelfBookingFeatureEnabled && !hasCompleteAddress) {
                    redirectToProfileSettings();
                    return;
                  }
                  dispatch(addEventToCart(eventData.id, currentUser.id));
                }
              }}
            >
              {eventData.participation_status === ParticipationStatus.Booked
                ? "bereits für mich gebucht"
                : isInCart(eventData.id)
                  ? "bereits in meinem Warenkorb"
                  : !checkTargetGroupMatch(currentUser)
                    ? "für mich*"
                    : "für mich"}
            </MenuItem>
            {!isAllowedToBook(true) &&
              eventData.participation_status !== ParticipationStatus.Booked && (
                <Tooltip
                  placement="right"
                  enterDelay={500}
                  leaveDelay={200}
                  title="Sie können nicht gleichzeitig für mehrere Nutzer buchen. Führen Sie zuerst den Buchungsvorgang durch oder leeren Sie den Warenkorb."
                  arrow
                >
                  <Info color="primary" style={{ cursor: "pointer" }} />
                </Tooltip>
              )}
          </Box>
          {/* TODO: maybe do the filering of the linked status in directly in backend, so only the linked accounts are being received instead of both linked and requested accounts */}
          {linkedAccountsLength > 0 &&
            linkAccount.linkAccountList
              .filter((x) => x.status === LinkAccountStatus.Linked)
              .map((linkedUserAccount, index) => {
                let statusText = `für ${linkedUserAccount.first_name} ${linkedUserAccount.last_name}`;
                switch (eventData.participation_status_linked_accounts[index].status) {
                  case ParticipationStatus.Booked:
                    statusText = `${linkedUserAccount.first_name} ${linkedUserAccount.last_name} nimmt bereits teil`;
                    break;
                  case ParticipationStatus.Participated:
                    statusText = `${linkedUserAccount.first_name} ${linkedUserAccount.last_name} hat bereits teilgenommen`;
                    break;
                  default:
                    if (isInCart(eventData.id, false, linkedUserAccount)) {
                      statusText = `bereits im Warenkorb von ${linkedUserAccount.first_name} ${linkedUserAccount.last_name}`;
                    } else if (!checkTargetGroupMatch(linkedUserAccount)) {
                      statusText = `für ${linkedUserAccount.first_name} ${linkedUserAccount.last_name}*`;
                    }
                }
                return (
                  <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={"linked-account-menu-item-" + index}
                    component="div"
                    display="flex"
                    alignItems="center"
                    onMouseEnter={(event) => {
                      setIsBookedForMe(false);

                      return !checkTargetGroupMatch(linkedUserAccount)
                        ? handlePopoverOpenWrongTargetGroup(event)
                        : undefined;
                    }}
                    onMouseLeave={
                      !checkTargetGroupMatch(linkedUserAccount)
                        ? handlePopoverCloseWrongTargetGroup
                        : undefined
                    }
                  >
                    <MenuItem
                      style={{
                        width:
                          !isAllowedToBook(false, linkedUserAccount) &&
                          eventData.participation_status_linked_accounts[index]
                            .status !== ParticipationStatus.Booked
                            ? "auto"
                            : "100%",
                      }}
                      disabled={
                        linkedAccountsLength > 0 &&
                        linkedUserAccount !== undefined &&
                        (!checkTargetGroupMatch(linkedUserAccount) ||
                          eventData.participation_status_linked_accounts[index]
                            .status === ParticipationStatus.Booked ||
                          eventIsFullyBooked ||
                          isInCart(eventData.id, false, linkedUserAccount) ||
                          !isAllowedToBook(false, linkedUserAccount))
                      }
                      onClick={(event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
                        handleBookingForClose();
                        dispatch(addEventToCart(eventData.id, linkedUserAccount.id));
                      }}
                    >
                      {statusText}
                    </MenuItem>
                    {!isAllowedToBook(false, linkedUserAccount) &&
                      eventData.participation_status_linked_accounts[index].status !==
                        ParticipationStatus.Booked && (
                        <Tooltip
                          placement="right"
                          enterDelay={500}
                          leaveDelay={200}
                          title="Sie können nicht gleichzeitig für mehrere Nutzer buchen. Führen Sie zuerst den Buchungsvorgang durch oder leeren Sie den Warenkorb."
                          arrow
                        >
                          <Info style={{ cursor: "pointer" }} color="primary" />
                        </Tooltip>
                      )}
                  </Box>
                );
              })}
        </Menu>
        <Box
          component="div"
          onMouseEnter={(event) =>
            linkedAccountsLength === 0 && !checkTargetGroupMatch(currentUser)
              ? handlePopoverOpenWrongTargetGroup(event)
              : undefined
          }
          onMouseLeave={
            linkedAccountsLength === 0 && !checkTargetGroupMatch(currentUser)
              ? handlePopoverCloseWrongTargetGroup
              : undefined
          }
        >
          {(!currentUser || currentUser?.user_type !== UserType.TestParticipant) && (
            <EventItemBookingButton
              id={setButtonName(props)}
              disabled={
                (linkAccount.linkAccountList.length === 0 &&
                  !checkTargetGroupMatch(currentUser)) ||
                eventData.status === EventStatus.Cancelled ||
                eventIsFullyBooked
              }
              color={eventIsFullyBooked ? "secondary" : "primary"}
              text={
                eventData.status === EventStatus.Cancelled
                  ? t("status.cancelled", { ns: "events" })
                  : eventIsFullyBooked
                    ? t("status.full", { ns: "events" })
                    : t("eventBooking.bookNow", { ns: "events" })
              }
              action={(event) => {
                if (!auth.loggedIn) {
                  history.push(routes.login);
                } else {
                  if (linkedAccountsLength !== 0) {
                    handleBookingForClick(event);
                  } else if (currentUser) {
                    if (userSelfBookingFeatureEnabled && !hasCompleteAddress) {
                      redirectToProfileSettings();
                      return;
                    }
                    dispatch(addEventToCart(eventData.id, currentUser.id));
                  }
                }
              }}
            />
          )}
        </Box>
      </>
    );
  } else {
    return null;
  }
};

export default EventItemRenderButton;
